import React, { Fragment } from 'react'
import { graphql } from 'gatsby'
import { keyframes } from '@emotion/core'
import Img from 'gatsby-image'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
  dropShadow,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, min, rem, selection, lines } from '../../styles/tools'

import { AutoFade, AutoSlide, AutoScale } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import Link from '../../components/Link'
import { Headline, BoldText, Detail } from '../../styles/typography'

import Logo from '../../assets/pages/work/dignity-health/Logo.js'

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/dignity-health.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/dignity-health.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/dignity-health.jpg" }) {
      ...twitterShareImage
    }
    devices: file(
      relativePath: { eq: "pages/work/dignity-health/devices.png" }
    ) {
      ...pageContainerWide
    }
    bg: file(relativePath: { eq: "pages/work/dignity-health/bg.png" }) {
      ...windowWide
    }
    ipad: file(relativePath: { eq: "pages/work/dignity-health/ipad.png" }) {
      ...pageContainerWide
    }
    webpage: file(
      relativePath: { eq: "pages/work/dignity-health/webpage.jpg" }
    ) {
      ...pageContainerWide
    }
  }
`

let project = getProject('dignity-health')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo play width={360} />,
  }

  const devices = data.devices.childImageSharp.fluid
  const bg = data.bg.childImageSharp.fluid
  const ipad = data.ipad.childImageSharp.fluid
  const webpage = data.webpage.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <Fragment>
            <AutoFade>
              <Title as="h1" css={{ maxWidth: '11em' }}>
                Discovering the healing power of awe
              </Title>
            </AutoFade>
            <Space y={32} />
            <AutoFade>
              <Paragraph>
                Surfing. It's all about connection. We created a microsite for
                the Hello Humankindness™ campaign to introduce Dignity Health to
                the surfing community as the title sponsor for The Pismo Beach
                Open, a World Surf League – Qualifying Series 1,000 Contest that
                put Pismo Beach on the map as one of the top California surf
                destinations.
              </Paragraph>
            </AutoFade>
          </Fragment>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <AutoSlide up>
          <Img fluid={devices} style={{ marginBottom: '-40vmin' }} />
        </AutoSlide>
      </ProjectIntro>
      <Wrapper padded>
        <Space y="40vmin" />
        <div
          css={{
            [media.max.tabletMd]: { padding: `0 ${rem(32)}` },
            textAlign: 'left',
          }}
        >
          <BackgroundText
            as="h4"
            css={{ [media.max.tablet]: { display: 'none' } }}
          >
            DESIGN
          </BackgroundText>
          <AutoFade css={{ marginTop: '-7vw' }}>
            <Img fluid={webpage} />
          </AutoFade>
        </div>
      </Wrapper>
      <Wrapper>
        <div
          css={{
            padding: '0 16.666% 20vw',
            background: `url(${bg.src}) center / cover`,
          }}
        >
          <Space y={{ mobile: 64, tablet: 128 }} />
          <AutoSlide up>
            <Img fluid={ipad} />
          </AutoSlide>
        </div>
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <div
            css={[
              lines('rgba(255, 255, 255, .06)'),
              {
                display: 'flex',
                alignItems: 'center',
                height: '130%',
                userSelect: 'none',
                backgroundColor: '#d6763a',
              },
            ]}
          >
            <div css={{ width: '200%', [media.tabletLg]: { width: '50%' } }}>
              <svg
                viewBox="0 0 20.6 18.5"
                fill="#b66431"
                css={{ margin: '20% 0 0 -30%' }}
              >
                <path d="M0.2,8.5C0,8.8-0.1,9.1,0.1,9.4c0,0.1,0.1,0.1,0.1,0.2c0.2,0.2,0.4,0.4,0.6,0.6c0,0,0,0,0.1-0.1c0,0,0,0,0,0 l0,0c0.2,0.2,0.4,0.3,0.6,0.4c1.9,1.3,4.5,1.2,6.4,0c0.1,0.1,0.1,0.2,0.2,0.3C8,11,8,11.1,8.1,11.2C5.9,12,4.4,14,4.3,16.3 c0,0.3,0,0.5,0,0.8c0,0.3,0,0.5,0.1,0.8c0.1,0.4,0.5,0.7,0.9,0.6c0.5-0.1,1-0.3,1.5-0.5c2.4-1.2,3.6-3.8,3.1-6.4  c-0.2-1-0.7-1.9-1.3-2.6c-1.8-2-4.6-2.5-7-1.3C1.1,7.9,0.6,8.2,0.2,8.5z M6.1,16.6c-0.1,0-0.2,0.1-0.3,0.1l0-0.3 c0.1-1.6,1.1-3.1,2.6-3.7C8.5,14.3,7.6,15.8,6.1,16.6z M6.7,9.4c-1.4,0.8-3.1,0.8-4.5-0.1L2,9.1C2,9.1,2.1,9,2.2,9 C3.7,8.3,5.4,8.4,6.7,9.4z M19.1,7.6c-2.4-1.2-5.2-0.7-7,1.3c-0.7,0.7-1.1,1.6-1.3,2.6c-0.6,2.6,0.7,5.2,3.1,6.4 c0.5,0.2,1,0.4,1.5,0.5c0.4,0.1,0.8-0.2,0.9-0.6c0-0.3,0.1-0.5,0.1-0.8c0-0.3,0-0.5,0-0.8c-0.1-2.3-1.6-4.3-3.8-5.2 c0-0.1,0.1-0.2,0.1-0.3c0.1-0.1,0.1-0.2,0.2-0.3c1.9,1.3,4.4,1.3,6.4,0c0.2-0.1,0.4-0.3,0.6-0.4l0,0c0,0,0,0,0,0c0,0,0,0,0.1,0.1 c0.2-0.2,0.4-0.4,0.6-0.6c0-0.1,0.1-0.1,0.1-0.2c0.2-0.3,0.1-0.7-0.2-0.9C20,8.2,19.5,7.9,19.1,7.6z M12.2,12.7 c1.5,0.6,2.5,2.1,2.6,3.7l0,0.3c-0.1,0-0.2-0.1-0.3-0.1C13,15.8,12.1,14.3,12.2,12.7z M18.4,9c0.1,0,0.2,0.1,0.2,0.1l-0.2,0.2  c-1.4,0.9-3.1,0.9-4.5,0.1C15.2,8.4,16.9,8.3,18.4,9z M15.9,0.6C15.8,0.3,15.5,0,15.2,0C15.1,0,15,0,15,0c-0.3,0.1-0.5,0.2-0.8,0.3 c0,0,0,0.1,0,0.1c0,0,0,0,0,0l0,0c-0.2,0.1-0.5,0.2-0.7,0.3c-2,1.2-3.1,3.4-2.9,5.7c-0.1,0-0.2,0-0.4,0c-0.1,0-0.2,0-0.4,0 c0.3-2.3-0.9-4.5-2.9-5.7C6.9,0.5,6.6,0.4,6.4,0.3C6.2,0.2,5.9,0.1,5.7,0C5.3-0.1,4.8,0.2,4.7,0.6c-0.1,0.5-0.2,1-0.2,1.5 c0,2.6,1.8,5,4.3,5.6c1,0.3,2,0.3,2.9,0c2.6-0.7,4.3-3,4.3-5.6C16.1,1.6,16,1.1,15.9,0.6z M6,2.1c0-0.1,0-0.2,0-0.3L6.3,2 c1.4,0.8,2.2,2.4,2.1,4C7,5.3,6,3.8,6,2.1z M12.2,5.9c-0.1-1.6,0.7-3.1,2.1-4l0.3-0.1c0,0.1,0,0.2,0,0.3C14.6,3.8,13.6,5.2,12.2,5.9 z" />
              </svg>
            </div>
          </div>
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '65vh',
              maxHeight: rem(600),
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title
              as="blockquote"
              css={{ maxWidth: '22em', textAlign: 'center' }}
            >
              WSL’s inaugural event drew hundreds of spectators to the Pismo
              Beach pier and around 40,000 viewers to the live webcast.
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
